<template>
  <QDialog
    v-model="openedInternal"
    :persistent="persistent"
    :maximized="maximized"
    :no-backdrop-dismiss="noBackdropDismiss"
    :no-esc-dismiss="noEscDismiss"
    :transition-hide="transitionHide"
    :transition-show="transitionShow"
    :position="position"
    :class="additionalClass"
    @hide="$emit('hide')"
    @show="$emit('show')"
    @before-show="$emit('beforeShow')"
  >
    <QCard
      class="extracadabra-modal"
      :class="[card.class, { 'has-img': header.base64Img || header.img || header.icon }]"
      :style="{ width: getSize + 'px' }"
    >
      <header>
        <QToolbar class="extracadabra-modal-header row items-start" :class="headerClasses">
          <section v-if="header.base64Img" class="extracadabra-modal-header-img">
            <img :src="header.base64Img" alt="extracadabra-modal-header-img" />
          </section>
          <section v-if="header.img" class="extracadabra-modal-header-img">
            <img :src="require(`@core/assets/img/${header.img}`)" alt="extracadabra-modal-header-img" />
          </section>
          <section v-if="header.icon" class="extracadabra-modal-header-img">
            <QIcon class="icon" :name="header.icon" :color="header.bgColor || 'primary'" size="66px" />
          </section>
          <QToolbarTitle v-if="header.title" :shrink="true">
            {{ $filters.whitespacedPunctuation(header.title) }}
          </QToolbarTitle>
          <QSpace />
          <QBtn v-if="!header.hideCloseBtn" v-close-popup flat round dense :color="getTextColor(header.bgColor)" icon="close" />
        </QToolbar>
      </header>

      <main :class="modalBody.class" class="extracadabra-modal-body">
        <section :class="body.class">
          <slot name="body" />
        </section>

        <footer :class="footer.class" class="q-mt-md">
          <slot name="footer" />
        </footer>
      </main>
    </QCard>
  </QDialog>
</template>

<script>
  import { colors } from "quasar";
  export default {
    props: {
      singleToolbarSafe: {
        type: Boolean,
        default: false,
      },
      doubleToolbarSafe: {
        type: Boolean,
        default: false,
      },
      opened: {
        type: Boolean,
        default: false,
      },
      quasarProps: {
        type: Object,
        default: () => {
          return {};
        },
      },
      header: {
        type: Object,
        default: () => {
          return {
            class: {
              img: null, // img name, must be stored in core/assets/img
              base64Img: null, // base64 img
              icon: null, // QIcon name prop (ex: 'fad fa-check')
              class: null,
              bgColor: "white",
              textColor: "dark",
              hideCloseBtn: false,
              title: null,
            },
          };
        },
      },
      card: {
        type: Object,
        default: () => {
          return { class: "" };
        },
      },
      modalBody: {
        type: Object,
        default: () => {
          return { class: "" };
        },
      },
      body: {
        type: Object,
        default: () => {
          return { class: "" };
        },
      },
      footer: {
        type: Object,
        default: () => {
          return { class: "" };
        },
      },
      size: {
        type: String,
        default: "md",
      },
    },
    emits: ["beforeShow", "hide", "show", "update:opened"],
    computed: {
      additionalClass() {
        // Use doubleToolbarSafe or singleToolbarSafe if case you need to keep top toolbars visible with fullscreen modals
        if (this.doubleToolbarSafe) {
          return "use-double-toolbar-safearea";
        }
        if (this.singleToolbarSafe) {
          return "use-single-toolbar-safearea";
        }
        return "";
      },
      persistent() {
        return this.quasarProps.persistent || false;
      },
      maximized() {
        return this.quasarProps.maximized === undefined ? this.$q.screen.lt.sm : this.quasarProps.maximized;
      },
      noEscDismiss() {
        return this.quasarProps.noEscDismiss || false;
      },
      noBackdropDismiss() {
        return this.quasarProps.noBackdropDismiss || false;
      },
      transitionHide() {
        return this.quasarProps.transitionHide || "slide-down";
      },
      transitionShow() {
        return this.quasarProps.transitionShow || "slide-up";
      },
      position() {
        return this.quasarProps.position || "standard";
      },
      openedInternal: {
        get() {
          return this.opened;
        },
        set(value) {
          this.$emit("update:opened", value);
        },
      },
      headerClasses() {
        let classes = this.header.class || "";
        classes += this.header.bgColor ? ` bg-${this.header.bgColor}` : " bg-white";
        classes += this.header.textColor ? ` text-${this.header.textColor}` : " text-dark";
        return classes;
      },
      getSize() {
        let modalWidth = "";
        switch (this.size) {
          case "xs":
            modalWidth = "300";
            break;
          case "md":
            modalWidth = "800";
            break;
          case "lg":
            modalWidth = "1024";
            break;
          case "xl":
            modalWidth = "1600";
            break;
          case "sm":
          default:
            modalWidth = "512";
        }
        return modalWidth;
      },
    },
    methods: {
      /**
       * Automatically determines a text color (black or white) that contrasts with a given background color.
       * This function uses relative luminance (WCAG standards) to ensure good readability.
       * https://www.w3.org/WAI/GL/wiki/Relative_luminance
       *
       * @param {string} backgroundColor - Background color in hexadecimal format (e.g., "#RRGGBB").
       * @returns {string} - Recommended text color: either "dark" (#000000) or "white" (#FFFFFF).
       *
       * @example
       * getTextColor("#ffcc00"); // Returns "dark"
       * getTextColor("#0000ff"); // Returns "white"
       */
      getTextColor(backgroundColor = "white") {
        const { getPaletteColor, hexToRgb } = colors;

        const hexaColorRegex = new RegExp(/#(([0-9a-fA-F]{2}){3,4}|([0-9a-fA-F]){3,4})/);
        const hexaColor = hexaColorRegex.test(backgroundColor) ? backgroundColor : getPaletteColor(backgroundColor);

        const { r, g, b } = hexToRgb(hexaColor);

        // Apply the transformation
        const luminance = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
        const L = 0.2126 * luminance(r / 255) + 0.7152 * luminance(g / 255) + 0.0722 * luminance(b / 255);

        // Return black or white based on luminance
        return L > 0.5 ? "dark" : "white";
      },
    },
  };
</script>
